/* Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Directive, OnDestroy, AfterViewInit, ElementRef, NgZone, Optional, Inject } from '@angular/core';
import { fromEvent, merge, Subject } from 'rxjs';
import { filter, mapTo, take, takeUntil, tap } from 'rxjs/operators';
import { isEventUserActivated } from 'utils/lib/web-events/synthetic-events';
import { NV_HIG_CONFIG, NvHigConfig } from '../hig.config';
import { UserActivationGuardService } from '../services/user-activation-guard/user-activation-guard.service';

@Directive({
    // select those link tag that loads web content in a new page
    selector: '[nvHyperLinkGuard], a[href], *[innerHTML], *[innerHtml]',
    standalone: true
})
export class HyperLinkUserActivationGuardDirective implements AfterViewInit, OnDestroy {
    private destroy$ = new Subject();
    private message: string;
    private isWarningDisplayed: boolean;
    private enable = false;

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        private userActivationGuardService: UserActivationGuardService,
        private ngZone: NgZone,
        @Optional() @Inject(NV_HIG_CONFIG) higConfig: NvHigConfig
    ) {
        this.enable = !!higConfig?.userActivationGuardConfig?.link;
        this.message = higConfig?.userActivationGuardConfig?.linkWarningMessage;
    }

    ngAfterViewInit() {
        if (!this.enable || !this.message) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            fromEvent(this.elementRef.nativeElement, 'click')
                .pipe(
                    filter(e => this.isLink(e.target as HTMLElement)),
                    filter(e => !isEventUserActivated(e)),
                    tap(() => {
                        this.displayWarningMessage();
                    }),
                    takeUntil(this.destroy$)
                )
                .subscribe(e => {
                    e.preventDefault();
                });
        });
    }

    private isLink(target: HTMLElement) {
        return target.tagName === 'A' && target.hasAttribute('href');
    }

    private displayWarningMessage() {
        if (this.isWarningDisplayed) {
            return;
        }

        const snackbarRef = this.userActivationGuardService.displayWarning(this.message);
        if (snackbarRef) {
            merge(snackbarRef.afterOpened().pipe(mapTo(true)), snackbarRef.afterDismissed().pipe(mapTo(false)))
                .pipe(take(2), takeUntil(this.destroy$))
                .subscribe(opened => {
                    this.isWarningDisplayed = opened;
                });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
